import React, { useState } from 'react';
import { User, Mail, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';

// Define the API base URL - Using domain and relative path
const API_BASE = 'https://storetoyou.ca/api';

// Alternative options:
// 1. Use completely relative URL (if served from same domain)
// const API_BASE = '/api';
// 2. Use environment variable (recommended for multi-environment setup)
// const API_BASE = process.env.REACT_APP_API_URL || 'https://storetoyou.ca/api';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    // Email validation - use simple validation
    if (!formData.email || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }

    // Password validation - keep it simple
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Create a simple payload with only what the server needs
      const payload = {
        email: formData.email.trim(),
        password: formData.password
      };
      
      // Only include name if it's not empty
      if (formData.name && formData.name.trim()) {
        payload.name = formData.name.trim();
      }
      
      console.log('Sending registration payload:', payload);
      
      // Send registration request with minimal data
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Log the raw response for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', [...response.headers.entries()]);
      
      // Try to parse JSON response (if available)
      let data = {};
      try {
        const textResponse = await response.text();
        console.log('Raw response text:', textResponse);
        
        if (textResponse) {
          data = JSON.parse(textResponse);
        }
      } catch (parseErr) {
        console.error('Failed to parse response:', parseErr);
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || `Registration failed with status ${response.status}`);
      }

      // Show success message and redirect
      alert('Registration successful! Please log in.');
      navigate('/login');
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center bg-light py-5">
        <div className="card shadow" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-body p-4">
            <h2 className="card-title text-center mb-2">Create Account</h2>
            <p className="text-center text-muted mb-4">Sign up for a StoreToyou account</p>

            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              
              <div className="bg-light p-4 rounded border mb-4">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label fw-bold">
                    Full Name (Optional)
                  </label>
                  <div className="input-group">
                    <span className="input-group-text border-2">
                      <User size={20} />
                    </span>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      className="form-control border-2"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label fw-bold">
                    Email address
                  </label>
                  <div className="input-group">
                    <span className="input-group-text border-2">
                      <Mail size={20} />
                    </span>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="form-control border-2"
                      placeholder="Enter your email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label fw-bold">
                    Password
                  </label>
                  <div className="input-group">
                    <span className="input-group-text border-2">
                      <Lock size={20} />
                    </span>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      className="form-control border-2"
                      placeholder="Create a password"
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                  <small className="text-muted">Must be at least 6 characters long</small>
                </div>

                <div className="mb-2">
                  <label htmlFor="confirmPassword" className="form-label fw-bold">
                    Confirm Password
                  </label>
                  <div className="input-group">
                    <span className="input-group-text border-2">
                      <Lock size={20} />
                    </span>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      className="form-control border-2"
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 mb-3 py-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>

              <p className="text-center mb-0">
                Already have an account?{' '}
                <a href="/login" className="text-decoration-none">
                  Sign in
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;